import { useState } from 'react';

export default function Login({ onLogin }) {
  const [email, setEmail]     = useState('');
  const [pass, setPass]       = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await onLogin(email, pass); }
    catch(err) { setError(err.message || 'Invalid email or password'); }
    finally { setLoading(false); }
  };

  const fieldStyle = (name) => ({
    width: '100%',
    padding: '11px 14px',
    background: 'rgba(255,255,255,0.07)',
    border: `1.5px solid ${focused === name ? 'rgba(196,98,26,0.8)' : 'rgba(255,255,255,0.12)'}`,
    borderRadius: 10,
    fontSize: 14,
    color: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxShadow: focused === name ? '0 0 0 3px rgba(196,98,26,0.14)' : 'none',
  });

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: '#0B1F3A',
      display: 'flex',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* ── Animated background ───────────────────────────── */}
      <div style={{ position:'absolute', inset:0, pointerEvents:'none', zIndex:0 }}>
        {/* Radial glows */}
        <div style={{ position:'absolute', top:'-20%', right:'5%', width:700, height:700, borderRadius:'50%', background:'radial-gradient(circle, rgba(196,98,26,0.1) 0%, transparent 65%)', animation:'float 9s ease-in-out infinite' }}/>
        <div style={{ position:'absolute', bottom:'-25%', left:'-5%', width:600, height:600, borderRadius:'50%', background:'radial-gradient(circle, rgba(29,179,115,0.05) 0%, transparent 65%)', animation:'float 12s ease-in-out 3s infinite' }}/>
        {/* Grid */}
        <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', opacity:0.035 }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="56" height="56" patternUnits="userSpaceOnUse">
              <path d="M 56 0 L 0 0 0 56" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)"/>
        </svg>
        {/* Vertical divider line */}
        <div style={{ position:'absolute', top:0, left:'48%', width:1, height:'100%', background:'linear-gradient(to bottom, transparent 0%, rgba(196,98,26,0.2) 25%, rgba(196,98,26,0.2) 75%, transparent 100%)' }}/>
      </div>

      {/* ── Left branding panel ────────────────────────────── */}
      <div style={{
        width: '48%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '52px 56px',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Logo */}
        <div>
          <img
            src="/pas_logo.png"
            alt="PAS Trinidad"
            style={{ height: 48, filter: 'brightness(0) invert(1)', opacity: 0.9 }}
            onError={e => { e.target.style.display='none'; }}
          />
        </div>

        {/* Headline */}
        <div>
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#C4621A',
            marginBottom: 16,
          }}>
            Quotation Management System
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 44,
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1.08,
            letterSpacing: '-0.025em',
            marginBottom: 20,
          }}>
            Precision in<br/>every quote.
          </h1>
          <p style={{
            fontSize: 15,
            color: 'rgba(255,255,255,0.38)',
            lineHeight: 1.8,
            maxWidth: 330,
          }}>
            Generate, track and send professional freight quotations across Ocean, Air, FCL and local delivery — all from one place.
          </p>
        </div>

        {/* Service icons */}
        <div style={{ display:'flex', gap:32, alignItems:'center' }}>
          {[['🚢','Ocean LCL'],['✈️','Air Freight'],['📦','FCL'],['📋','Brokerage']].map(([icon, label]) => (
            <div key={label} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
              <span style={{ fontSize: 22 }}>{icon}</span>
              <span style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 8,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.22)',
              }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right form panel ───────────────────────────────── */}
      <div style={{
        width: '52%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 56px',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{
          width: '100%',
          maxWidth: 420,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.09)',
          borderRadius: 24,
          padding: '44px 40px',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          animation: 'fadeUp 0.5s cubic-bezier(0.16,1,0.3,1) both',
          boxShadow: '0 32px 80px rgba(0,0,0,0.4)',
        }}>
          {/* Form header */}
          <div style={{ marginBottom: 32 }}>
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.3)',
              marginBottom: 10,
            }}>Welcome back</div>
            <div style={{
              fontFamily: 'var(--font-display)',
              fontSize: 26,
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
            }}>Sign in to your<br/>account</div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: 'rgba(224,59,59,0.14)',
              border: '1px solid rgba(224,59,59,0.3)',
              borderRadius: 10,
              padding: '11px 14px',
              fontSize: 13,
              color: '#ff8888',
              marginBottom: 20,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}>
              <span>⚠</span> {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div>
              <label style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.35)',
                display: 'block',
                marginBottom: 8,
              }}>Email address</label>
              <input
                type="email"
                value={email}
                required
                autoFocus
                placeholder="you@pastrinidad.com"
                onChange={e => setEmail(e.target.value)}
                onFocus={() => setFocused('email')}
                onBlur={() => setFocused('')}
                style={fieldStyle('email')}
              />
            </div>

            <div>
              <label style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.35)',
                display: 'block',
                marginBottom: 8,
              }}>Password</label>
              <input
                type="password"
                value={pass}
                required
                placeholder="••••••••"
                onChange={e => setPass(e.target.value)}
                onFocus={() => setFocused('pass')}
                onBlur={() => setFocused('')}
                style={fieldStyle('pass')}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 10,
                padding: '13px',
                background: loading
                  ? 'rgba(255,255,255,0.08)'
                  : 'linear-gradient(135deg, #C4621A 0%, #E07A38 100%)',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                letterSpacing: '0.01em',
                boxShadow: loading ? 'none' : '0 6px 20px rgba(196,98,26,0.4)',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
              onMouseEnter={e => { if (!loading) e.target.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.target.style.transform = ''; }}
            >
              {loading ? (
                <>
                  <div style={{ width:16, height:16, border:'2px solid rgba(255,255,255,0.2)', borderTop:'2px solid #fff', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
                  Signing in…
                </>
              ) : 'Sign in →'}
            </button>
          </form>

          {/* Footer note */}
          <div style={{
            marginTop: 28,
            paddingTop: 20,
            borderTop: '1px solid rgba(255,255,255,0.07)',
            fontSize: 11,
            color: 'rgba(255,255,255,0.2)',
            textAlign: 'center',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.04em',
          }}>
            PAS TRINIDAD LIMITED © {new Date().getFullYear()}
          </div>
        </div>
      </div>
    </div>
  );
}
